import { useTreeStateContext } from '../../contexts/TreeContext';
import { useEffect } from "react";
import generateObjectId from '../../utils/generateObjectId';
import editorAction from '/src/features/editor/actions/editorAction';
import cellSizeEditor from '../../data/cellSizeEditor';
import binarySearchString from "../../utils/binarySearchString";
import createBlock from '../../utils/createBlock';
import BlocksTransformerList from '../../utils/transformers/BlocksTransformerList';

function useBlock() {

    const { addBlock : addBlockAction, deleteBlock, updateBlock, addBlockTemplate : addBlockTemplateAction } = editorAction();

    const { blocks, paths } = useTreeStateContext();

    const addBlock = (data, x, y, _x, _y) => {
        const { block, blockServer, children } = createBlock(data, x, y, _x, _y)
        addBlockAction(block, blockServer, children);
    }

    const updateBlockAction = (block_id, data) => {
        var { tree : treeBlock, server : blockServer } = data;

        if(treeBlock.children == undefined) {
            updateBlock(block_id, treeBlock, blockServer, []);
            return { ...treeBlock }
        }

        const children = [];
        for(var i = 0; i < treeBlock.children.length; i++) {
            treeBlock.children[i].parent_block_id = block_id
            treeBlock.children[i].object_id = generateObjectId(i);
            treeBlock.children[i].index = i + 1;
            if(treeBlock.children[i].id) {
                delete treeBlock.children[i].id;
            }
            let child = treeBlock.children[i];
            children.push(child);
        }

        if (blockServer.action_type === 'categorization' && Array.isArray(blockServer.branches) && blockServer.branches.length === 0) {
            const branches = children.map((child, idx) => ({
                branch_id: child.object_id,
                values: [],
                forbidden: [],
                is_default: idx === children.length - 1,
            }));
            blockServer.branches = branches;
            treeBlock.data = { ...treeBlock.data, branches };
        }

        updateBlock(block_id, {...treeBlock}, blockServer, children);
        return {...treeBlock}
    }

    const addBlockTemplate = (data, x, y, coord) => {
        const objectId = generateObjectId();
        let width  = Math.round( (coord.max.x - coord.min.x) / cellSizeEditor );
        let height = Math.round( (coord.max.y - coord.min.y) / cellSizeEditor );

        const block = {object_id : objectId, width, height, x :  -x + 20, y :  -y , data, type : { name : 'template' } }
        addBlockTemplateAction(block);
    }

    const getBlockInformation = (id)  => {

        const index = binarySearchString(blocks, id, 'object_id');
    
        const block = blocks[index];

        if(block == null) return {};
    
        const paths_out = paths.filter( path => path.parent_block_id == id);
    
        const paths_in = block.children ? block.children.map(child => paths.find( path => path.child_block_id == child.object_id)).filter( path => path != undefined) : [];
    
        return {block, paths_out, paths_in, position : index}
    }

    const duplicateBlock = async (id) => {

        const index = binarySearchString(blocks, id, 'object_id');

        var block = blocks[index];
        const type = block.type.name == 'action' && block.action_type ? block.action_type.name : block.type.name

        block = JSON.parse(JSON.stringify(block))

        const data = { ...block.data }
        delete data.id
        delete data.block_id

        if (block.type.name == "magic" && data.jump_id == null){ // on ne doit pas pouvoir duplicate quand il n'y a pas de link
            return
        }

        delete block.bot_category_id
        delete block._x
        delete block._y

        if (Array.isArray(block.children)) {
            block.children.sort((a, b) => a.index - b.index);
        }
        const originalChildIds = (block.children || []).map(child => child.object_id);

        const transformer = BlocksTransformerList[type];
        const blockServer = { ...transformer(data), name : block.name, 'type' : block.type.name}
        if(block.action_type) {
            blockServer.action_type = block.action_type.name;
        }

        const object = {tree : block, server : blockServer }
        const { block: newBlock, blockServer: newBlockServer, children: newChildren } = createBlock(object, -block.x, -block.y, 250, 0)

        if (type === 'categorization' && Array.isArray(newBlockServer.branches)) {
            const newChildByIndex = {};
            newChildren.forEach(child => { newChildByIndex[child.index] = child; });

            const idMap = {};
            originalChildIds.forEach((oldId, i) => {
                if (newChildByIndex[i]) idMap[oldId] = newChildByIndex[i].object_id;
            });
            const remapBranchId = (branch) => ({ ...branch, branch_id: idMap[branch.branch_id] ?? branch.branch_id });

            newBlockServer.branches = newBlockServer.branches.map(remapBranchId);
            if (newBlock.data && Array.isArray(newBlock.data.branches)) {
                newBlock.data.branches = newBlock.data.branches.map(remapBranchId);
            }
        }

        await addBlockAction(newBlock, newBlockServer, newChildren);
    }

    const jumpBlock = (targetBlockId) => {
        const index = binarySearchString(blocks, targetBlockId, "object_id");
        const targetBlock = blocks[index];
        if (!targetBlock) return;

        
        const newJumpBlock = {
            name: `Jump to ${targetBlock.name}`,
            type: { name: "magic" }, // defines it as a Jump-type block
            width: 5,
            height: 2,
            x: targetBlock.x + 250, // slightly offset to the right
            y: targetBlock.y,
            data: {
                jump_id: targetBlock, // link to target block
                is_constant: true,
                variable_value: null,
            },
        };

        
        const transformer = BlocksTransformerList["magic"];
    
        const blockServer = {
            ...transformer(newJumpBlock.data),
            name: newJumpBlock.name,
            type: newJumpBlock.type.name,
        };

    
        const blockObject = { tree: newJumpBlock, server: blockServer };
        
        addBlock(blockObject, -targetBlock.x, -targetBlock.y, 250, 0);
    };

    const getChildPosition = (parent_block_id, child_block_id) => {
        
        let index = binarySearchString(blocks, parent_block_id);
        let block = blocks[index];

        let child = block.children.filter(child => child.object_id == child_block_id).pop();
        if(child == undefined) {
            console.log(child_block_id, block.children)
        }
        
        let height = block.height * cellSizeEditor;
        let width =  block.width  * cellSizeEditor;
        let x = block.x + width - (cellSizeEditor / 2);
        let y;
        if (child.index == 0) {
            y = block.y + (block.height * cellSizeEditor) / 2 + (cellSizeEditor / 2);
        }
        else if (block.action_type && block.action_type.name === 'categorization') {
            const renderPosition = [...block.children].sort((a, b) => a.index - b.index).findIndex(c => c.object_id === child_block_id);
            y = block.y + cellSizeEditor * (renderPosition + 3);
        }
        else {
            y = block.y + height + (cellSizeEditor * child.index);
        }

        return {x , y}
    }
    useEffect(() => {
        window.getBlockInformation = getBlockInformation;

    }, [blocks])
    return { addBlock, addBlockTemplate, updateBlockAction, getBlockInformation, getChildPosition, deleteBlock, duplicateBlock, jumpBlock}
}

export default useBlock;